import { isAppleDevice } from "@react-aria/utils";
import { useMemo } from "react";
import { type ComponentsTranslationKey, useComponentsTranslation } from "../i18n/i18next.ts";

export interface ResolvedShortcut {
    keys: string[];
    ariaKeyShortcuts: string;
    matchesEvent: (event: KeyboardEvent) => boolean;
}

const MODIFIER_GLYPHS = {
    Mod: { apple: "⌘", other: "Shortcut.mod" },
    Shift: { apple: "⇧", other: "Shortcut.shift" },
    Alt: { apple: "⌥", other: "Shortcut.alt" }
} as const satisfies Record<string, { apple: string; other: ComponentsTranslationKey }>;

const MODIFIER_ARIA_NAMES = {
    Mod: { apple: "Meta", other: "Control" },
    Shift: { apple: "Shift", other: "Shift" },
    Alt: { apple: "Alt", other: "Alt" }
} as const;

const KEY_LABELS: Partial<Record<string, ComponentsTranslationKey>> = {
    Escape: "Shortcut.escape"
};

type Modifier = keyof typeof MODIFIER_GLYPHS;

function isModifier(key: string): key is Modifier {
    return key in MODIFIER_GLYPHS;
}

export function useShortcut(descriptor: string): ResolvedShortcut {
    const isApple = isAppleDevice();
    const { t } = useComponentsTranslation();

    return useMemo(() => {
        const platform = isApple ? "apple" : "other";
        const segments = descriptor.split("+");
        const modifiers = segments.filter(isModifier);
        const [key, ...extraKeys] = segments.filter(segment => !isModifier(segment));

        if (key === undefined || extraKeys.length > 0) {
            throw new Error(`useShortcut: "${descriptor}" must name exactly one non-modifier key.`);
        }

        const upperKey = key.length === 1 ? key.toUpperCase() : key;
        const keyLabelKey = KEY_LABELS[key];

        return {
            keys: [
                ...modifiers.map(modifier =>
                    isApple ? MODIFIER_GLYPHS[modifier].apple : t(MODIFIER_GLYPHS[modifier].other)
                ),
                keyLabelKey ? t(keyLabelKey) : upperKey
            ],
            ariaKeyShortcuts: [...modifiers.map(modifier => MODIFIER_ARIA_NAMES[modifier][platform]), upperKey].join(
                "+"
            ),
            matchesEvent: (event: KeyboardEvent) =>
                event.key.toLowerCase() === key.toLowerCase() &&
                (isApple ? event.metaKey : event.ctrlKey) === modifiers.includes("Mod") &&
                event.shiftKey === modifiers.includes("Shift") &&
                event.altKey === modifiers.includes("Alt")
        };
    }, [descriptor, isApple, t]);
}
